import * as FromUser from "src/app/store/user/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import * as FromScenario from "src/app/store/scenario/selectors";
import * as ScenarioActions from "src/app/store/scenario/actions";
import { ChangeDetectionStrategy, Component, HostListener, ViewEncapsulation } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { AppRoutes, CHANCE_NODES_PER_PAGE, RUNS_PER_PAGE, TOAST_DEFAULT_DURATION } from "src/app/constants";
import { RootState } from "src/app/store";
import { first, withLatestFrom } from "rxjs/operators";
import { ChanceNodeActionDto, ChanceNodeDto, ChanceNodeRunDto, UserDto } from "@api";
import { AlertController, InfiniteScrollCustomEvent, IonRouterOutlet, ModalController, ToastController } from "@ionic/angular";
import { ScenarioPage as ScenarioModal } from "src/app/modals/scenario/scenario.page";
import { ActionSelectorPage } from "src/app/modals/action-selector/action-selector.page";
import { TranslateService } from "@ngx-translate/core";
import { ScenarioLogsPage } from "src/app/modals/scenario-logs/scenario-logs.page";
import { MultiActionPage } from "src/app/modals/multi-action/multi-action.page";

@Component({
  selector: "app-scenario",
  templateUrl: "./scenario.page.html",
  styleUrls: ["./scenario.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ScenarioPage {
  public routes: typeof AppRoutes = AppRoutes;
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public loading$: Observable<boolean> = this.store.select(FromScenario.getLoading);
  public scenario$: Observable<ChanceNodeDto> = this.store.select(FromScenario.getScenario);
  public history$: Observable<ChanceNodeRunDto[]> = this.store.select(FromScenario.getScenarioRuns);
  public currentHistoryPage$: Observable<number> = this.store.select(FromScenario.getScenarioHistoryCurrentPage);
  public currentHistoryTotalPages$: Observable<number> = this.store.select(FromScenario.getScenarioHistoryTotalPages);

  public breadcrumbs = [
    { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
    { name: this.i18n.instant("SCENARIOS.BREADCRUMB_NAME"), link: `/${this.routes.scenarios}` },
    { name: this.i18n.instant("SCENARIOS.DETAILS") },
  ];

  public tabs = [
    { name: this.i18n.instant("SCENARIOS.FLOW"), active: true },
    { name: this.i18n.instant("SCENARIOS.HISTORY"), active: false },
  ];

  constructor(
    private store: Store<RootState>,
    private i18n: TranslateService,
    private modalController: ModalController,
    private alertController: AlertController,
    private toastController: ToastController,
    private routerOutlet: IonRouterOutlet,
  ) {}

  @HostListener("window:message", ["$event"])
  protected onMessage(event: any): Promise<void> {
    const { type, actionId, chanceNodeId, chanceNodeActionId, reason, parentId } = event.data;

    switch (type) {
      case "ADD_ACTION":
        return this.addAction(reason, chanceNodeId);

      case "EDIT_ACTION":
        this.store
          .select(FromScenario.getScenarioById(chanceNodeId))
          .pipe(first())
          .subscribe(chanceNode =>
            this.editAction(
              chanceNode.actions.find(chanceNodeAction => chanceNodeAction.id === chanceNodeActionId),
              reason,
              actionId,
              chanceNodeId,
            ),
          );
        break;

      case "OPEN_MULTIACTION":
        this.openMultiActionModal(chanceNodeId, reason);
        break;

      case "ADD_SCENARIO":
        return this.addScenario(reason, parentId);

      case "EDIT_SCENARIO":
        this.store
          .select(FromScenario.getScenarioById(chanceNodeId))
          .pipe(first())
          .subscribe(scenario => this.editScenario(scenario));
        break;

      case "DEACTIVE_SCENARIO":
        this.deactiveScenario();
        break;

      case "REACTIVE_SCENARIO":
        this.reactiveScenario();
        break;

      case "RUN_SCENARIO":
        this.runScenario();
        break;

      default:
        break;
    }
  }

  public activeTab(index: number): void {
    this.tabs.forEach(tab => (tab.active = false));
    this.tabs[index].active = true;
    this.store
      .select(FromRouter.selectScenarioId)
      .pipe(first())
      .subscribe(chanceNodeId => this.store.dispatch(ScenarioActions.getScenarioRuns({ chanceNodeId, limit: RUNS_PER_PAGE, page: 1 })));
  }

  public async addScenario(reason: boolean, parentId: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ScenarioModal,
      componentProps: { reason, parentId },
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  public async editScenario(scenario: ChanceNodeDto): Promise<void> {
    const modal = await this.modalController.create({
      component: ScenarioModal,
      componentProps: { scenario },
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  public async addAction(reason: boolean, chanceNodeId: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ActionSelectorPage,
      componentProps: { reason, chanceNodeId },
      cssClass: ["--select-action-modal"],
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  public async editAction(chanceNodeAction: ChanceNodeActionDto, reason: boolean, actionId: string, chanceNodeId: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ActionSelectorPage,
      componentProps: { chanceNodeAction, reason, actionId, chanceNodeId, parameters: chanceNodeAction.parameters },
      cssClass: ["--select-action-modal"],
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  public async openLogs(run: ChanceNodeRunDto): Promise<void> {
    this.store
      .select(FromRouter.selectScenarioId)
      .pipe(first())
      .subscribe(async chanceNodeId => {
        const modal = await this.modalController.create({
          component: ScenarioLogsPage,
          componentProps: { runId: run.runId, chanceNodeId },
          cssClass: ["chance-node-logs-modal"],
          showBackdrop: true,
          presentingElement: this.routerOutlet.nativeEl,
          swipeToClose: true,
        });

        await modal.present();
      });
  }

  public loadMoreRuns(event: InfiniteScrollCustomEvent): void {
    this.currentHistoryPage$
      .pipe(withLatestFrom(this.store.select(FromRouter.selectScenarioId)), first())
      .subscribe(([currentPage, chanceNodeId]) =>
        this.store.dispatch(ScenarioActions.getScenarioRuns({ chanceNodeId, limit: RUNS_PER_PAGE, page: currentPage + 1, event })),
      );
  }

  protected ionViewDidEnter(): void {
    this.store
      .select(FromRouter.selectScenarioId)
      .pipe(first())
      .subscribe(chanceNodeId => {
        this.store.dispatch(ScenarioActions.getScenarioRuns({ chanceNodeId, limit: RUNS_PER_PAGE, page: 1 }));
        this.store.dispatch(ScenarioActions.getChanceNodes({ limit: CHANCE_NODES_PER_PAGE, page: 1 }));
        this.store.dispatch(ScenarioActions.getTriggers());
        this.store.dispatch(ScenarioActions.getActions());
      });
  }

  private deactiveScenario(): void {
    this.scenario$.pipe(first()).subscribe(async scenario => {
      if (!scenario.active) {
        const toast = await this.toastController.create({
          message: this.i18n.instant("SCENARIOS.ALREADY_INACTIVE"),
          color: "dark",
          duration: TOAST_DEFAULT_DURATION,
        });
        await toast.present();
      } else {
        const alert = await this.alertController.create({
          header: this.i18n.instant("SCENARIOS.DEACTIVE_ALERT.HEADER"),
          message: this.i18n.instant("SCENARIOS.DEACTIVE_ALERT.MESSAGE"),
          buttons: [
            { text: this.i18n.instant("BUTTONS.CANCEL"), role: "cancel" },
            {
              text: this.i18n.instant("BUTTONS.YES"),
              role: "destructive",
              handler: (): void =>
                this.store.dispatch(
                  ScenarioActions.updateChanceNodeOnScenario({
                    chanceNodeId: scenario.id,
                    updateChanceNodeDto: { ...scenario, active: false },
                  }),
                ),
            },
          ],
        });

        await alert.present();
      }
    });
  }

  private async reactiveScenario(): Promise<void> {
    this.scenario$.pipe(first()).subscribe(async scenario => {
      if (scenario.active) {
        const toast = await this.toastController.create({
          message: this.i18n.instant("SCENARIOS.ALREADY_ACTIVE"),
          color: "dark",
          duration: TOAST_DEFAULT_DURATION,
        });
        await toast.present();
      } else {
        const alert = await this.alertController.create({
          header: this.i18n.instant("SCENARIOS.REACTIVE_ALERT.HEADER"),
          message: this.i18n.instant("SCENARIOS.REACTIVE_ALERT.MESSAGE"),
          buttons: [
            { text: this.i18n.instant("BUTTONS.CANCEL"), role: "cancel" },
            {
              text: this.i18n.instant("BUTTONS.YES"),
              role: "destructive",
              handler: (): void =>
                this.store.dispatch(
                  ScenarioActions.updateChanceNodeOnScenario({
                    chanceNodeId: scenario.id,
                    updateChanceNodeDto: { ...scenario, active: true },
                  }),
                ),
            },
          ],
        });

        await alert.present();
      }
    });
  }

  private async openMultiActionModal(chanceNodeId: string, reason: boolean): Promise<void> {
    const modal = await this.modalController.create({
      component: MultiActionPage,
      componentProps: { chanceNodeId, reason },
      cssClass: ["multiple-actions-modal"],
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  private async runScenario(): Promise<void> {
    this.store
      .select(FromRouter.selectScenarioId)
      .pipe(first())
      .subscribe(async chanceNodeId => {
        const modal = await this.modalController.create({
          component: ScenarioLogsPage,
          componentProps: { chanceNodeId },
          cssClass: ["chance-node-logs-modal"],
          showBackdrop: true,
          presentingElement: this.routerOutlet.nativeEl,
          swipeToClose: true,
        });

        await modal.present();
      });
  }
}
